import { z } from "zod";

// Model comp-card measurements. People write these many ways ("5'9", "175 cm",
// "34in", "86 cm"), so we parse common formats, check sensible ranges, and
// save one consistent format designers can fit samples from.

const CM_PER_INCH = 2.54;
const round = (n: number, step = 0.5) => Math.round(n / step) * step;
const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/** Parses a height into total inches. Accepts 5'9", 5 ft 9 in, 5-9, 175 cm, 1.75 m, 69 in. */
export function parseHeightInches(value: string): number | null {
  const v = value.trim().toLowerCase().replace(/[’′]/g, "'").replace(/[”″]/g, '"');
  let m = v.match(/^(\d(?:\.\d+)?)\s*m$/);
  if (m) return Number(m[1]) * 100 / CM_PER_INCH;
  m = v.match(/^(\d{2,3}(?:\.\d+)?)\s*(cm|centimet(er|re)s?)$/);
  if (m) return Number(m[1]) / CM_PER_INCH;
  m = v.match(/^(\d)\s*(?:'|ft|feet|foot)\s*(?:(\d{1,2}(?:\.\d+)?)\s*(?:"|''|in|inch|inches)?)?$/);
  if (m) return Number(m[1]) * 12 + Number(m[2] ?? 0);
  m = v.match(/^(\d)\s*-\s*(\d{1,2})$/);
  if (m) return Number(m[1]) * 12 + Number(m[2]);
  m = v.match(/^(\d{2}(?:\.\d+)?)\s*(?:"|in|inch|inches)$/);
  if (m) return Number(m[1]);
  return null;
}

/** Parses a body measurement into inches. Plain numbers are inches; "cm" is converted. */
export function parseMeasurementInches(value: string): number | null {
  const v = value.trim().toLowerCase().replace(/[”″"]/g, " in");
  const m = v.match(/^(\d{2,3}(?:\.\d+)?)\s*(in|inch|inches|cm|centimet(?:er|re)s?)?$/);
  if (!m) return null;
  const n = Number(m[1]);
  return m[2]?.startsWith("c") ? n / CM_PER_INCH : n;
}

export function formatHeight(inches: number): string {
  const total = Math.round(inches);
  return `${Math.floor(total / 12)}'${total % 12}" (${Math.round(inches * CM_PER_INCH)} cm)`;
}

const preprocessString = (fn: (v: string) => string) => (v: unknown) => (typeof v === "string" ? fn(v) : v);

export const HEIGHT_ERROR = "Please enter your height, for example 5'9\" or 175 cm.";
export const zodHeight = () =>
  z.preprocess(
    preprocessString((v) => {
      const inches = parseHeightInches(v);
      return inches !== null && inches >= 54 && inches <= 87 ? formatHeight(inches) : v;
    }),
    z.string().regex(/^\d'\d{1,2}" \(\d{3} cm\)$/, HEIGHT_ERROR),
  );

/** Bust or chest, waist, hips, inseam: saved as inches with the cm value. */
export const zodMeasurement = (name: string, min: number, max: number, optional = false) => {
  const message = `Please enter your ${name} in inches, for example ${Math.round((min + max) / 2)}. You can also write cm, for example ${Math.round(((min + max) / 2) * CM_PER_INCH)} cm.`;
  return z.preprocess(
    preprocessString((v) => {
      if (optional && !v.trim()) return "";
      const inches = parseMeasurementInches(v);
      return inches !== null && inches >= min && inches <= max
        ? `${fmt(round(inches))} in (${Math.round(inches * CM_PER_INCH)} cm)`
        : v;
    }),
    z.string().refine((s) => (optional && s === "") || /^\d+(\.5)? in \(\d+ cm\)$/.test(s), { message }),
  );
};

export const SHOE_SIZE_ERROR = "Please enter a US or Canadian shoe size from 3 to 16, for example 8.5.";
export const zodShoeSize = () =>
  z.preprocess(
    preprocessString((v) => v.trim().replace(/^(us|ca|size)\s*/i, "").replace(",", ".")),
    z.string().refine((s) => /^\d{1,2}(\.5)?$/.test(s) && Number(s) >= 3 && Number(s) <= 16, { message: SHOE_SIZE_ERROR }),
  );
